import * as THREE from 'three';
import { PuzzleElement } from './PuzzleElement';

export class GuardianStatue extends PuzzleElement {
  private detectionRange: number = 8;
  private detectionAngle: number = Math.PI / 3; // 60 degrees
  private facingDirection: THREE.Vector3;
  private isPlayerDetected: boolean = false;
  private eyeGlow: THREE.PointLight;
  private onPlayerDetected: (() => void) | null = null;
  
  constructor(
    position: THREE.Vector3, 
    facingDirection: THREE.Vector3,
    onPlayerDetected?: () => void
  ) {
    super(position);
    this.facingDirection = facingDirection.clone().normalize();
    this.onPlayerDetected = onPlayerDetected || null;
    
    // Rotate mesh to face direction
    const angle = Math.atan2(facingDirection.x, facingDirection.z);
    this.mesh.rotation.y = angle;
    
    // Add eye glow
    this.eyeGlow = new THREE.PointLight(0xff0000, 0, 5);
    this.eyeGlow.position.set(0, 2.5, 0.3);
    this.mesh.add(this.eyeGlow);
  }
  
  protected createMesh(): THREE.Group {
    const group = new THREE.Group();
    
    // Body
    const bodyGeometry = new THREE.BoxGeometry(1.5, 2.5, 1);
    const stoneMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x4a4a4a,
      roughness: 0.9,
      metalness: 0.1
    });
    
    const body = new THREE.Mesh(bodyGeometry, stoneMaterial);
    body.position.y = 1.25;
    body.castShadow = true;
    body.receiveShadow = true;
    group.add(body);
    
    // Head
    const headGeometry = new THREE.BoxGeometry(0.8, 0.8, 0.8);
    const head = new THREE.Mesh(headGeometry, stoneMaterial);
    head.position.y = 2.9;
    head.castShadow = true;
    group.add(head);
    
    // Eyes
    const eyeGeometry = new THREE.SphereGeometry(0.1, 8, 8);
    const eyeMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000 });
    
    const leftEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
    leftEye.position.set(-0.2, 2.9, 0.4);
    group.add(leftEye);
    
    const rightEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
    rightEye.position.set(0.2, 2.9, 0.4);
    group.add(rightEye);
    
    return group;
  }
  
  public update(deltaTime: number, playerPosition: THREE.Vector3, isPlayerInvisible: boolean = false): void {
    // Check if player is in detection cone
    const toPlayer = playerPosition.clone().sub(this.position);
    toPlayer.y = 0; // Ignore height difference
    const distance = toPlayer.length();
    
    if (distance < this.detectionRange && !isPlayerInvisible) {
      toPlayer.normalize();
      const dot = toPlayer.dot(this.facingDirection);
      const angle = Math.acos(Math.min(1, Math.max(-1, dot)));
      
      this.isPlayerDetected = angle < this.detectionAngle;
    } else {
      this.isPlayerDetected = false;
    }
    
    // Update eye glow
    this.eyeGlow.intensity = this.isPlayerDetected ? 2 : 0.3;
    
    // Fire callback if detected
    if (this.isPlayerDetected) {
      this.onPlayerDetected?.();
    }
  }
  
  public isDetectingPlayer(): boolean {
    return this.isPlayerDetected;
  }
  
  public setDetectionCallback(callback: () => void): void {
    this.onPlayerDetected = callback;
  }
}
